'use client';

import {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  Dispatch,
} from 'react';

interface ProductState {
  isCreateProductPopupOpen: boolean;
  isEditProductPopupOpen: boolean;
  productId: string | null;
}

type ProductAction =
  | { type: 'OPEN_CREATE_PRODUCT_POPUP' }
  | { type: 'CLOSE_CREATE_PRODUCT_POPUP' }
  | { type: 'OPEN_EDIT_PRODUCT_POPUP'; payload: { productId: string } }
  | { type: 'CLOSE_EDIT_PRODUCT_POPUP' };

const initialState: ProductState = {
  isCreateProductPopupOpen: false,
  isEditProductPopupOpen: false,
  productId: null,
};

const ProductContext = createContext<{
  state: ProductState;
  dispatch: Dispatch<ProductAction>;
} | null>(null);

function productReducer(
  state: ProductState,
  action: ProductAction
): ProductState {
  switch (action.type) {
    case 'OPEN_CREATE_PRODUCT_POPUP':
      return { ...state, isCreateProductPopupOpen: true };
    case 'CLOSE_CREATE_PRODUCT_POPUP':
      return { ...state, isCreateProductPopupOpen: false };
    case 'OPEN_EDIT_PRODUCT_POPUP':
      return {
        ...state,
        isEditProductPopupOpen: true,
        productId: action.payload.productId,
      };
    case 'CLOSE_EDIT_PRODUCT_POPUP':
      return { ...state, isEditProductPopupOpen: false, productId: null };
    default:
      return state;
  }
}

export const ProductProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(productReducer, initialState);
  return (
    <ProductContext.Provider value={{ state, dispatch }}>
      {children}
    </ProductContext.Provider>
  );
};

export const useProductContext = () => {
  const context = useContext(ProductContext);
  if (!context)
    throw new Error('useProductContext must be used within ProductProvider');
  return context;
};
